import React, { useEffect, useRef, useState } from 'react';
import { formatMetricaDate, parseUserDateInput, toIsoDateOnly, toIsoDateString } from '../../utils/dateUtils';
import styles from './MetricaDateInput.module.css';

type MetricaDateInputProps = {
  value: string;
  onChange: (isoDate: string) => void;
  ariaLabel?: string;
  invalid?: boolean;
};

const MetricaDateInput: React.FC<MetricaDateInputProps> = ({
  value,
  onChange,
  ariaLabel,
  invalid = false,
}) => {
  const isoValue = toIsoDateOnly(value);
  const pickerRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState(isoValue ? formatMetricaDate(isoValue) : '');
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (focused) return;
    setDraft(isoValue ? formatMetricaDate(isoValue) : '');
  }, [isoValue, focused]);

  const commitText = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) {
      onChange('');
      setDraft('');
      return;
    }
    const parsed = parseUserDateInput(trimmed);
    if (!parsed) {
      setDraft(isoValue ? formatMetricaDate(isoValue) : '');
      return;
    }
    const iso = toIsoDateString(parsed);
    onChange(iso);
    setDraft(formatMetricaDate(iso));
  };

  const openCalendar = () => {
    const picker = pickerRef.current;
    if (!picker) return;
    try {
      const pickerWithDialog = picker as HTMLInputElement & { showPicker?: () => void };
      if (typeof pickerWithDialog.showPicker === 'function') {
        pickerWithDialog.showPicker();
        return;
      }
    } catch {
      // Fall through to click() when showPicker is blocked.
    }
    picker.click();
  };

  return (
    <div className={styles.wrap}>
      <input
        type="text"
        className={`${styles.textInput} ${invalid ? styles.invalid : ''}`.trim()}
        value={draft}
        placeholder="dd-mmm-yy"
        dir="ltr"
        autoComplete="off"
        spellCheck={false}
        aria-label={ariaLabel}
        onChange={(event) => setDraft(event.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          commitText(draft);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            (event.target as HTMLInputElement).blur();
          }
        }}
      />
      <button
        type="button"
        className={styles.calendarButton}
        onMouseDown={(event) => event.preventDefault()}
        onClick={openCalendar}
        aria-label="פתח לוח שנה"
        title="בחירה מלוח שנה"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"
            fill="currentColor"
          />
        </svg>
      </button>
      <input
        ref={pickerRef}
        type="date"
        className={styles.hiddenPicker}
        value={isoValue}
        tabIndex={-1}
        aria-hidden
        onChange={(event) => {
          const next = event.target.value;
          onChange(next);
          setDraft(next ? formatMetricaDate(next) : '');
        }}
      />
    </div>
  );
};

export default MetricaDateInput;
